import { createContext, useContext, useState } from 'react'

import { useQuery } from '@tanstack/react-query'

import { getT, toISO8601 } from '@fortem-monorepo/js-utilities'
import { useApi } from '@fortem-monorepo/react-spa'
import { MarketSummary } from '@fortem-monorepo/web-sdk'

import { useUserContext } from '@/index'

export const MARKET_SUMMARY_TABS = {
  DISPLAYS: 'displays',
  INSTRUMENTS: 'instruments',
  EVENTS: 'events',
} as const

export const DISPLAY_TABS = {
  INSTRUMENTS: 'instruments',
  REPORT: 'report',
  METRICS: 'metrics',
} as const

export type MarketSummaryTab = (typeof MARKET_SUMMARY_TABS)[keyof typeof MARKET_SUMMARY_TABS]
export type DisplayTab = (typeof DISPLAY_TABS)[keyof typeof DISPLAY_TABS]

export const useCreateMarketSummaryContext = () => {
  const [view, selectView] = useState<MarketSummary.View | null>(null)
  const [reportDates, setReportDates] = useState<Record<number, Date>>({})
  const displayId = view?.display_id ?? 1
  const value_date = reportDates[displayId] ?? getT()
  const setValueDate = (date: Date) => setReportDates((dates) => ({ ...dates, [displayId]: date }))
  const viewSelected = typeof view?.display_id === 'number' && view.display_id >= 0

  const [tab, selectTab] = useState<MarketSummaryTab>(MARKET_SUMMARY_TABS.DISPLAYS)
  const [displayTab, selectDisplayTab] = useState<DisplayTab>(DISPLAY_TABS.REPORT)

  const api = useApi()

  const { hasPermissions } = useUserContext()

  const hasWriteAccess = hasPermissions((permissions) => ({ permissions: [permissions.WRITE_MARKET_SUMMARY] }))

  // ---------------------------------

  const viewsQuery = useQuery({
    queryKey: ['market-summary', 'displays'],
    queryFn: () => api.marketSummary.getDisplays(),
  })

  // ---------------------------------

  const instrumentsQuery = useQuery({
    queryKey: ['market-summary', 'instruments'],
    queryFn: () => api.marketSummary.listAllInstruments(),
  })

  // ---------------------------------

  const displayInstrumentsQuery = useQuery({
    queryKey: ['market-summary', 'display-instruments', view?.display_id],
    queryFn: () => api.marketSummary.display(view!.display_id).instruments.listAll(),
    enabled: viewSelected,
  })

  const eventsQuery = useQuery({
    queryKey: ['market-summary', 'events', view?.display_id],
    queryFn: () => api.marketSummary.listAllEvents(),
  })

  // ---------------------------------

  const reportQuery = useQuery({
    queryKey: ['market-summary', 'report', { display_id: view?.display_id!, value_date: toISO8601(value_date) }],
    queryFn: () => api.marketSummary.display(view?.display_id!).getReport({ value_date: toISO8601(value_date) }),
    enabled: viewSelected,
  })

  const reportSections = [...new Set((reportQuery.data?.block.rows ?? []).map((s) => s.section))]

  // ---------------------------------

  const metricsQuery = useQuery({
    queryKey: ['market-summary', 'metrics', view?.display_id],
    queryFn: () => api.marketSummary.display(view?.display_id!).metrics.listAll(),
    enabled: viewSelected,
  })

  return {
    view,
    selectView,
    viewSelected,
    value_date,
    setValueDate,
    tab,
    selectTab,
    displayTab,
    selectDisplayTab,
    viewsQuery,
    reportQuery,
    reportSections,
    instrumentsQuery,
    displayInstrumentsQuery,
    metricsQuery,
    eventsQuery,
    hasWriteAccess,
  }
}

export const marketSummaryContext = createContext<ReturnType<typeof useCreateMarketSummaryContext> | null>(null)

export const useMarketSummaryContext = () => {
  const context = useContext(marketSummaryContext)
  if (!context) throw new Error('Market Summary Context can only be using within a <MarketSummaryProvider/>.')
  return context
}

export interface MarketSummaryProviderProps extends React.PropsWithChildren {}

export const MarketSummaryProvider: React.FC<MarketSummaryProviderProps> = ({ children }) => {
  const context = useCreateMarketSummaryContext()

  return <marketSummaryContext.Provider value={context}>{children}</marketSummaryContext.Provider>
}
